import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setDynamicSegment } from '../store/slices/organizationsSlice';
import { setReturnUrl, setAppName } from '../store/slices/appContextSlice';
import { isAllowedReturnUrl } from './returnUrlValidator';
import { acceptsReturnUrl } from './appReturnUrl';
import { oidc } from '../config/oidc';

// sessionStorage keys for returnUrl/appName: when the user lands unauthenticated
// with these in the URL, signinRedirect() navigates away and the params are
// lost. After the OIDC callback the URL only carries `?code=...&state=...`, so
// we round-trip via sessionStorage (tab-scoped, cleared when the tab closes —
// localStorage would be too sticky across unrelated visits).
const RETURN_URL_STORAGE_KEY = 'orgadmin_return_url';
const APP_NAME_STORAGE_KEY = 'orgadmin_app_name';

const safeSessionSet = (key: string, value: string) => {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // sessionStorage may be unavailable (private mode quota, disabled storage)
        // — non-fatal; the user just won't see the round-tripped value after auth.
    }
};

const safeSessionGet = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

export const QueryParamHandler = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const hasRun = useRef(false);

    useEffect(() => {
        if (!hasRun.current) {
            const queryParams = new URLSearchParams(location.search);
            const org = queryParams.get('org');
            const returnUrl = queryParams.get('returnUrl');
            const appName = queryParams.get('appName');
            const passwordReset = queryParams.get('password_reset');

            // Handle organization query parameter
            // Only override stored segment when explicitly provided
            if (org) {
                dispatch(setDynamicSegment(org));
            }

            // Handle return-url to the calling application; fall back to VITE_DEFAULT_APP_URL.
            // Validate against an allowlist so the "Back to app" button can't be turned
            // into an open redirect by a crafted ?returnUrl= value. Persist to
            // sessionStorage on first sight so the value survives the OIDC round-trip;
            // re-validate on read because the stored value is still user-influenced.
            // Two questions, deliberately separate: acceptsReturnUrl() asks whether this
            // app has a companion application at all — false for an API-only build, where
            // a crafted ?returnUrl= must not be able to conjure a back-to-app button onto
            // a product with nothing behind it — and isAllowedReturnUrl() asks whether a
            // given URL is somewhere we may point.
            if (!acceptsReturnUrl()) {
                // Nothing to store and nothing to restore: leave the initial '' in place.
            } else if (returnUrl && isAllowedReturnUrl(returnUrl)) {
                safeSessionSet(RETURN_URL_STORAGE_KEY, returnUrl);
                dispatch(setReturnUrl(returnUrl));
            } else {
                const storedReturnUrl = safeSessionGet(RETURN_URL_STORAGE_KEY);
                if (storedReturnUrl && isAllowedReturnUrl(storedReturnUrl)) {
                    dispatch(setReturnUrl(storedReturnUrl));
                }
            }
            if (appName) {
                // setAppName reducer enforces the length cap; pass the raw value.
                safeSessionSet(APP_NAME_STORAGE_KEY, appName);
                dispatch(setAppName(appName));
            } else {
                const storedAppName = safeSessionGet(APP_NAME_STORAGE_KEY);
                if (storedAppName) {
                    dispatch(setAppName(storedAppName));
                }
            }

            // Handle password reset success
            if (passwordReset === 'success') {
                // Store success flag in sessionStorage to show notification after login
                sessionStorage.setItem('passwordResetSuccess', 'true');

                // Clean up the URL parameter (cosmetic - may not complete before redirect)
                // Note: This is best-effort cleanup. The sessionStorage flag is the source of truth.
                queryParams.delete('password_reset');
                const newSearch = queryParams.toString();
                const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

                // Replace URL without reloading
                window.history.replaceState({}, '', newUrl);

                // Initiate OAuth2 login flow (navigates away immediately)
                oidc.loginWithRedirect();
            }

            hasRun.current = true;
        }
    }, [location, dispatch]);

    return null;
};
